/**
 * AISidebar · 可折叠的受控宽度面板壳
 *
 * 对应文档：docs/AI-Photo-Category/DEV_BRIEF.md §2.2 / §2.3
 *            docs/AI-Photo-Category/TECH_PLAN.md §4.2.2
 *
 * 职责：
 *  · 提供右侧可折叠面板容器，仅呈现
 *  · 内容通过 children 承载（通常是 <AIChat />）
 *  · 宽度完全受控：由页面持有，通过 width prop 注入
 *
 * 不做：
 *  · 不内嵌 ResizeHandle（拖拽手柄由页面作为独立 flex item 渲染）
 *  · 不持有 width / isDragging 等状态（拖拽粘滞处理由页面通过 className 注入）
 *  · 不依赖 AIChat（children 由使用方组装）
 *  · 不接 Redux
 *  · 不使用 AntD 3 Drawer
 */

import React from 'react'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import { CollapseIcon } from '../SidebarHeaderIcons'
import styles from './AISidebar.module.scss'

export interface AISidebarProps {
  /** 显隐 · 收起时宽度收敛到 0 */
  visible: boolean
  /** 收起按钮回调 · 标题左侧 double-right 触发 */
  onClose: () => void
  /** 宽度（px）· 受控 · 由页面通过 useResizable 管理 */
  width: number
  /** 面板内容 · 由页面组装（如 <AIChat />） */
  children?: React.ReactNode
  /** 外部自定义类名 · 页面在拖拽态传入以关闭 width transition */
  className?: string
  /**
   * 头部右侧按钮组 · 与 AIChatDrawer 共享 AIChatHeaderActions 让两边视觉/行为完全一致
   * 不传时右侧为空，仅保留左侧标题 + 收起按钮（向后兼容）
   */
  headerActions?: React.ReactNode
}

const AISidebar: React.FC<AISidebarProps> = ({
  visible,
  onClose,
  width,
  children,
  className,
  headerActions
}) => (
  <div
    className={classNames(styles.root, className, {
      [styles.rootHidden]: !visible
    })}
    // 关闭时宽度 → 0，由 .root 的 transition 统一驱动开关动画
    style={{ width: visible ? width : 0 }}
    aria-hidden={!visible}
  >
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.headerLeft}>
          {/**
           * 收起按钮放在标题左侧 · 语义对齐"折叠 panel"而不是"关闭 / 退出"
           *  · 图标为双箭头 »（Figma 12:2915），指向折叠方向（面板向右收起）
           *  · 与列表页右上角的 AITriggerButton（展开入口）形成开/合的视觉对称
           */}
          <Tooltip title="收起 AI 助手" placement="bottomLeft">
            <span
              className={styles.collapseBtn}
              onClick={onClose}
              role="button"
              aria-label="收起 AI 助手"
            >
              <CollapseIcon />
            </span>
          </Tooltip>
        </span>
        {/**
         * 标题绝对定位居中 · 不受左右两侧按钮宽度影响（保持几何中心）
         *  · pointer-events:none 让标题不挡住底层（虽然现在没有，但留出 hit-test 余量）
         *  · max-width 兜底防长标题与左右按钮重叠
         */}
        <span className={styles.title}>AI 智能分类助手</span>
        <span className={styles.headerRight}>{headerActions}</span>
      </div>
      <div className={styles.body}>{children}</div>
    </div>
  </div>
)

export default AISidebar

// 附属组件二次导出 · AITriggerButton 仅页面头部使用，保留便利
export { default as AITriggerButton } from './AITriggerButton'
